import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Select,
  Switch,
  Table,
  Modal,
  message,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  InputNumber,
  Popconfirm,
  Divider,
  Alert
} from 'antd';
import {
  SettingOutlined,
  ApiOutlined,
  SafetyOutlined,
  GlobalOutlined,
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface ApiKey {
  _id: string;
  name: string;
  description?: string;
  keyPrefix: string;
  permissions: string[];
  isActive: boolean;
  usageCount: number;
  lastUsedAt?: string;
  createdAt: string;
}

const SystemSettings: React.FC = () => {
  const [generalForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [smsForm] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [webhookForm] = Form.useForm();
  const [apiKeyForm] = Form.useForm();
  const [securityForm] = Form.useForm();
  
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [apiKeyModalVisible, setApiKeyModalVisible] = useState(false);

  const fetchSettings = async () => {
    try {
      const data = await stationOwnerAPI.getSystemSettings();
      
      // Form'ları doldur
      if (data.general) {
        generalForm.setFieldsValue(data.general);
      }
      if (data.integrations?.email) {
        emailForm.setFieldsValue({
          ...data.integrations.email,
          smtpPassword: '***' // Şifre gösterilmez
        });
      }
      if (data.integrations?.sms) {
        smsForm.setFieldsValue({
          ...data.integrations.sms,
          apiKey: data.integrations.sms.apiKey ? '***' : '',
          apiSecret: '***'
        });
      }
      if (data.integrations?.payment) {
        paymentForm.setFieldsValue({
          ...data.integrations.payment,
          apiKey: data.integrations.payment.apiKey ? '***' : '',
          secretKey: '***'
        });
      }
      if (data.integrations?.webhooks) {
        webhookForm.setFieldsValue({
          ...data.integrations.webhooks,
          secret: '***'
        });
      }
      if (data.security) {
        securityForm.setFieldsValue(data.security);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Ayarlar yüklenemedi');
    }
  };

  const fetchApiKeys = async () => {
    try {
      const data = await stationOwnerAPI.getApiKeys();
      setApiKeys(data);
    } catch (error: any) {
      message.error('API Key\'ler yüklenemedi');
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchApiKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleGeneralSave = async (values: any) => {
    setLoading(true);
    try {
      await stationOwnerAPI.updateSystemSettings({ general: values });
      message.success('Genel ayarlar kaydedildi');
      fetchSettings();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Ayarlar kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSave = async (values: any) => {
    setLoading(true);
    try {
      // Şifre değişmemişse gönderme
      if (values.smtpPassword === '***') {
        delete values.smtpPassword;
      }
      await stationOwnerAPI.updateSystemSettings({ integrations: { email: values } });
      message.success('E-posta ayarları kaydedildi');
      fetchSettings();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Ayarlar kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSmsSave = async (values: any) => {
    setLoading(true);
    try {
      if (values.apiKey === '***') delete values.apiKey;
      if (values.apiSecret === '***') delete values.apiSecret;
      await stationOwnerAPI.updateSystemSettings({ integrations: { sms: values } });
      message.success('SMS ayarları kaydedildi');
      fetchSettings();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Ayarlar kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSave = async (values: any) => {
    setLoading(true);
    try {
      if (values.apiKey === '***') delete values.apiKey;
      if (values.secretKey === '***') delete values.secretKey;
      await stationOwnerAPI.updateSystemSettings({ integrations: { payment: values } });
      message.success('Ödeme ayarları kaydedildi');
      fetchSettings();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Ayarlar kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleWebhookSave = async (values: any) => {
    setLoading(true);
    try {
      if (values.secret === '***') delete values.secret;
      await stationOwnerAPI.updateSystemSettings({ integrations: { webhooks: values } });
      message.success('Webhook ayarları kaydedildi');
      fetchSettings();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Ayarlar kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSecuritySave = async (values: any) => {
    setLoading(true);
    try {
      await stationOwnerAPI.updateSystemSettings({ security: values });
      message.success('Güvenlik ayarları kaydedildi');
      fetchSettings();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Ayarlar kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async (values: any) => {
    setLoading(true);
    try {
      const response = await stationOwnerAPI.createApiKey(values);
      setNewApiKey(response.key);
      message.success('API Key oluşturuldu');
      setApiKeyModalVisible(true);
      apiKeyForm.resetFields();
      fetchApiKeys();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'API Key oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteApiKey = async (id: string) => {
    try {
      await stationOwnerAPI.deleteApiKey(id);
      message.success('API Key silindi');
      fetchApiKeys();
    } catch (error: any) {
      message.error('API Key silinemedi');
    }
  };

  const handleToggleApiKey = async (id: string) => {
    try {
      await stationOwnerAPI.toggleApiKeyStatus(id);
      message.success('API Key durumu değiştirildi');
      fetchApiKeys();
    } catch (error: any) {
      message.error('İşlem başarısız');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Panoya kopyalandı');
  };

  const apiKeyColumns = [
    {
      title: 'Ad',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-'
    },
    {
      title: 'Key',
      dataIndex: 'keyPrefix',
      key: 'keyPrefix',
      render: (prefix: string) => (
        <Space>
          <Text code>{prefix}...</Text>
          <Button
            type="link"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(prefix)}
          />
        </Space>
      )
    },
    {
      title: 'İzinler',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (permissions: string[]) => (
        <Space wrap>
          {permissions.map(p => (
            <Tag key={p}>{p}</Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      )
    },
    {
      title: 'Kullanım',
      dataIndex: 'usageCount',
      key: 'usageCount',
      render: (count: number) => count || 0
    },
    {
      title: 'Son Kullanım',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-'
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: ApiKey) => (
        <Space>
          <Button
            size="small"
            onClick={() => handleToggleApiKey(record._id)}
          >
            {record.isActive ? 'Pasifleştir' : 'Aktifleştir'}
          </Button>
          <Popconfirm
            title="API Key'i silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteApiKey(record._id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Sil
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const tabItems = [
    {
      key: 'general',
      label: (
        <span>
          <GlobalOutlined />
          Genel Ayarlar
        </span>
      ),
      children: (
        <Card>
          <Form
            form={generalForm}
            layout="vertical"
            onFinish={handleGeneralSave}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="companyName" label="Şirket Adı">
                  <Input placeholder="Şirket adınızı girin" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="contactEmail" label="İletişim E-postası">
                  <Input type="email" placeholder="email@example.com" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="contactPhone" label="İletişim Telefonu">
                  <Input placeholder="+90 555 123 45 67" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="timezone" label="Zaman Dilimi">
                  <Select placeholder="Zaman dilimi seçiniz">
                    <Option value="Europe/Istanbul">Türkiye (GMT+3)</Option>
                    <Option value="Europe/London">Londra (GMT+0)</Option>
                    <Option value="America/New_York">New York (GMT-5)</Option>
                    <Option value="Asia/Dubai">Dubai (GMT+4)</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="currency" label="Para Birimi">
                  <Select placeholder="Para birimi seçiniz">
                    <Option value="TRY">TRY - Türk Lirası</Option>
                    <Option value="USD">USD - Dolar</Option>
                    <Option value="EUR">EUR - Euro</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="dateFormat" label="Tarih Formatı">
                  <Select placeholder="Tarih formatı seçiniz">
                    <Option value="DD.MM.YYYY">DD.MM.YYYY</Option>
                    <Option value="MM/DD/YYYY">MM/DD/YYYY</Option>
                    <Option value="YYYY-MM-DD">YYYY-MM-DD</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="timeFormat" label="Saat Formatı">
                  <Select placeholder="Saat formatı seçiniz">
                    <Option value="24h">24 Saat</Option>
                    <Option value="12h">12 Saat (AM/PM)</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="address" label="Adres">
                  <TextArea rows={3} placeholder="Şirket adresinizi girin" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Kaydet
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )
    },
    {
      key: 'integrations',
      label: (
        <span>
          <SettingOutlined />
          Entegrasyonlar
        </span>
      ),
      children: (
        <Tabs
          type="card"
          items={[
            {
              key: 'email',
              label: 'E-posta',
              children: (
                <Card>
                  <Form
                    form={emailForm}
                    layout="vertical"
                    onFinish={handleEmailSave}
                  >
                    <Form.Item name="enabled" valuePropName="checked">
                      <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
                      <Text style={{ marginLeft: 8 }}>E-posta entegrasyonunu etkinleştir</Text>
                    </Form.Item>
                    <Divider />
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item name="smtpHost" label="SMTP Sunucu">
                          <Input placeholder="smtp.gmail.com" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="smtpPort" label="SMTP Port">
                          <InputNumber placeholder="587" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="smtpUser" label="SMTP Kullanıcı Adı">
                          <Input placeholder="email@example.com" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="smtpPassword" label="SMTP Şifre">
                          <Input.Password placeholder="Şifrenizi girin" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="smtpSecure" valuePropName="checked">
                          <Switch />
                          <Text style={{ marginLeft: 8 }}>Güvenli Bağlantı (SSL/TLS)</Text>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="fromEmail" label="Gönderen E-posta">
                          <Input type="email" placeholder="noreply@example.com" />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item name="fromName" label="Gönderen Adı">
                          <Input placeholder="YakıtGo" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item>
                      <Button type="primary" htmlType="submit" loading={loading}>
                        Kaydet
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>
              )
            },
            {
              key: 'sms',
              label: 'SMS',
              children: (
                <Card>
                  <Form
                    form={smsForm}
                    layout="vertical"
                    onFinish={handleSmsSave}
                  >
                    <Form.Item name="enabled" valuePropName="checked">
                      <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
                      <Text style={{ marginLeft: 8 }}>SMS entegrasyonunu etkinleştir</Text>
                    </Form.Item>
                    <Divider />
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item name="provider" label="SMS Sağlayıcı">
                          <Select placeholder="Sağlayıcı seçiniz">
                            <Option value="netgsm">NetGSM</Option>
                            <Option value="twilio">Twilio</Option>
                            <Option value="custom">Özel</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="sender" label="Gönderen">
                          <Input placeholder="YAKITGO" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="apiKey" label="API Key">
                          <Input.Password placeholder="API Key'inizi girin" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="apiSecret" label="API Secret">
                          <Input.Password placeholder="API Secret'inizi girin" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item>
                      <Button type="primary" htmlType="submit" loading={loading}>
                        Kaydet
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>
              )
            },
            {
              key: 'payment',
              label: 'Ödeme',
              children: (
                <Card>
                  <Form
                    form={paymentForm}
                    layout="vertical"
                    onFinish={handlePaymentSave}
                  >
                    <Form.Item name="enabled" valuePropName="checked">
                      <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
                      <Text style={{ marginLeft: 8 }}>Ödeme entegrasyonunu etkinleştir</Text>
                    </Form.Item>
                    <Divider />
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item name="provider" label="Ödeme Sağlayıcı">
                          <Select placeholder="Sağlayıcı seçiniz">
                            <Option value="iyzico">İyzico</Option>
                            <Option value="paytr">PayTR</Option>
                            <Option value="stripe">Stripe</Option>
                            <Option value="custom">Özel</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="merchantId" label="Mağaza ID">
                          <Input placeholder="Mağaza ID'nizi girin" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="apiKey" label="API Key">
                          <Input.Password placeholder="API Key'inizi girin" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="secretKey" label="Secret Key">
                          <Input.Password placeholder="Secret Key'inizi girin" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item>
                      <Button type="primary" htmlType="submit" loading={loading}>
                        Kaydet
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>
              )
            },
            {
              key: 'webhooks',
              label: 'Webhooks',
              children: (
                <Card>
                  <Form
                    form={webhookForm}
                    layout="vertical"
                    onFinish={handleWebhookSave}
                  >
                    <Form.Item name="enabled" valuePropName="checked">
                      <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
                      <Text style={{ marginLeft: 8 }}>Webhook entegrasyonunu etkinleştir</Text>
                    </Form.Item>
                    <Divider />
                    <Row gutter={16}>
                      <Col xs={24}>
                        <Form.Item name="url" label="Webhook URL">
                          <Input placeholder="https://example.com/webhook" />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item name="secret" label="Webhook Secret">
                          <Input.Password placeholder="Secret key'inizi girin" />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item name="events" label="Etkinlikler">
                          <Select mode="multiple" placeholder="Etkinlikleri seçiniz">
                            <Option value="campaign.created">Kampanya Oluşturuldu</Option>
                            <Option value="campaign.updated">Kampanya Güncellendi</Option>
                            <Option value="order.created">Sipariş Oluşturuldu</Option>
                            <Option value="payment.completed">Ödeme Tamamlandı</Option>
                            <Option value="customer.created">Müşteri Oluşturuldu</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item>
                      <Button type="primary" htmlType="submit" loading={loading}>
                        Kaydet
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>
              )
            }
          ]}
        />
      )
    },
    {
      key: 'api-keys',
      label: (
        <span>
          <ApiOutlined />
          API Anahtarları
        </span>
      ),
      children: (
        <Card
          title="API Anahtarları"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => apiKeyForm.resetFields()}
            >
              Yeni API Key
            </Button>
          }
        >
          <Form
            form={apiKeyForm}
            layout="vertical"
            onFinish={handleCreateApiKey}
            style={{ marginBottom: 24 }}
          >
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="name"
                  label="Ad"
                  rules={[{ required: true, message: 'Ad gerekli' }]}
                >
                  <Input placeholder="API Key adı" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="description" label="Açıklama">
                  <Input placeholder="Açıklama (opsiyonel)" />
                </Form.Item>
              </Col>
              <Col xs={24} md={4}>
                <Form.Item label=" ">
                  <Button type="primary" htmlType="submit" loading={loading} block>
                    Oluştur
                  </Button>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item name="permissions" label="İzinler">
                  <Select mode="multiple" placeholder="İzinleri seçiniz">
                    <Option value="read:stations">İstasyonları Oku</Option>
                    <Option value="write:stations">İstasyonları Yaz</Option>
                    <Option value="read:campaigns">Kampanyaları Oku</Option>
                    <Option value="write:campaigns">Kampanyaları Yaz</Option>
                    <Option value="read:customers">Müşterileri Oku</Option>
                    <Option value="write:customers">Müşterileri Yaz</Option>
                    <Option value="read:reports">Raporları Oku</Option>
                    <Option value="write:reports">Raporları Yaz</Option>
                    <Option value="read:analytics">Analitikleri Oku</Option>
                    <Option value="write:analytics">Analitikleri Yaz</Option>
                    <Option value="read:invoices">Faturaları Oku</Option>
                    <Option value="write:invoices">Faturaları Yaz</Option>
                    <Option value="read:orders">Siparişleri Oku</Option>
                    <Option value="write:orders">Siparişleri Yaz</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
          
          <Table
            columns={apiKeyColumns}
            dataSource={apiKeys}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
      )
    },
    {
      key: 'security',
      label: (
        <span>
          <SafetyOutlined />
          Güvenlik
        </span>
      ),
      children: (
        <Card>
          <Form
            form={securityForm}
            layout="vertical"
            onFinish={handleSecuritySave}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="twoFactorAuth.enabled" valuePropName="checked">
                  <Switch />
                  <Text style={{ marginLeft: 8 }}>İki Faktörlü Kimlik Doğrulama</Text>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="sessionTimeout" label="Oturum Süresi (dakika)">
                  <InputNumber min={5} max={480} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Divider orientation="left">Şifre Politikası</Divider>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="passwordPolicy.minLength" label="Minimum Şifre Uzunluğu">
                  <InputNumber min={6} max={32} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="passwordPolicy.requireUppercase" valuePropName="checked">
                  <Switch />
                  <Text style={{ marginLeft: 8 }}>Büyük Harf Gerekli</Text>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="passwordPolicy.requireLowercase" valuePropName="checked">
                  <Switch />
                  <Text style={{ marginLeft: 8 }}>Küçük Harf Gerekli</Text>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="passwordPolicy.requireNumbers" valuePropName="checked">
                  <Switch />
                  <Text style={{ marginLeft: 8 }}>Rakam Gerekli</Text>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="passwordPolicy.requireSpecialChars" valuePropName="checked">
                  <Switch />
                  <Text style={{ marginLeft: 8 }}>Özel Karakter Gerekli</Text>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Divider orientation="left">Giriş Güvenliği</Divider>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="loginAttempts.maxAttempts" label="Maksimum Deneme Sayısı">
                  <InputNumber min={3} max={10} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="loginAttempts.lockoutDuration" label="Kilitlenme Süresi (dakika)">
                  <InputNumber min={5} max={1440} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Kaydet
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )
    }
  ];

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <Title level={2} style={{ marginBottom: '24px' }}>
        Sistem Ayarları
      </Title>

      <Tabs items={tabItems} />

      <Modal
        title="Yeni API Key Oluşturuldu"
        open={apiKeyModalVisible}
        onCancel={() => {
          setApiKeyModalVisible(false);
          setNewApiKey(null);
        }}
        footer={[
          <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={() => {
            if (newApiKey) copyToClipboard(newApiKey);
          }}>
            Kopyala
          </Button>,
          <Button key="close" onClick={() => {
            setApiKeyModalVisible(false);
            setNewApiKey(null);
          }}>
            Kapat
          </Button>
        ]}
      >
        <Alert
          message="Önemli"
          description="Bu API Key'i güvenli bir yerde saklayın. Bir daha gösterilmeyecektir!"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Input
          value={newApiKey || ''}
          readOnly
          suffix={
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => {
                if (newApiKey) copyToClipboard(newApiKey);
              }}
            />
          }
        />
      </Modal>
    </div>
  );
};

export default SystemSettings;

